import { useWallet } from '../contexts/WalletContext'
import MessagingPanel from '../components/MessagingPanel'
import { MessageCircle, Shield, Lock } from 'lucide-react'

export default function Messages() {
  const { isConnected } = useWallet()

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Connect Your Wallet</h1>
          <p className="text-gray-600 mb-8">
            Connect your Sui wallet to access secure messaging features.
          </p>
          <button className="btn-primary">
            Connect Wallet
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Secure Messaging</h1>
          <div className="flex items-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-2 text-green-500" />
              <span>End-to-end encrypted</span>
            </div>
            <div className="flex items-center">
              <Lock className="w-4 h-4 mr-2 text-blue-500" />
              <span>On-chain storage</span>
            </div>
            <div className="flex items-center">
              <MessageCircle className="w-4 h-4 mr-2 text-purple-500" />
              <span>Decentralized</span>
            </div>
          </div>
        </div>

        {/* Messaging Panel */}
        <div className="h-[600px]">
          <MessagingPanel />
        </div>

        {/* Features Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">End-to-End Encryption</h3>
            <p className="text-gray-600 text-sm">
              All messages are encrypted using Seal technology, ensuring only you and the recipient can read them.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">On-Chain Storage</h3>
            <p className="text-gray-600 text-sm">
              Messages are stored on the Sui blockchain, providing verifiability and permanent access.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Decentralized Attachments</h3>
            <p className="text-gray-600 text-sm">
              File attachments are stored on Walrus decentralized storage with cryptographic guarantees.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-12 bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How Sui Stack Messaging Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Connect Wallet</h3>
              <p className="text-sm text-gray-600">
                Connect your Sui wallet to access your decentralized identity
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Start Conversation</h3>
              <p className="text-sm text-gray-600">
                Begin a secure conversation with any Sui wallet address
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Encrypt & Send</h3>
              <p className="text-sm text-gray-600">
                Messages are encrypted with Seal and stored on-chain
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                4
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secure Access</h3>
              <p className="text-sm text-gray-600">
                Only authorized participants can decrypt and read messages
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
